import React, { useEffect, useRef, useState } from "react";
import clsx from "clsx";
import { MagnifyingGlassIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { useDebouncedValue } from "@/shared/hooks";

type Props = {
  /** Legacy — auto-search has no submit button so this is no longer surfaced. Kept for API compatibility. */
  loading?: boolean;
  /** Optional controlled value. When provided (together with onChange) the
   *  parent owns the text — needed so a panel's "Clear" button can reset the
   *  box. Omit both to keep the input uncontrolled (internal state). */
  value?: string;
  onChange?: (q: string) => void;
  onSearch: (q: string) => void;
  onClear: () => void;
  className?: string;
};

/**
 * Type-to-search input shared by the widget Search panels (Headlines, Firehose,
 * StocksRatings, ResearchHeadlines). Mirrors the visual of the watchlist/filter
 * search bar: magnifying-glass on the left, in-input X to clear on the right.
 * Typing fires onSearch automatically after a short debounce; Enter submits
 * immediately and always fires (even when empty) so a date-only search can run.
 * Optionally controllable via value/onChange.
 */
function SearchBar(props: Props) {
  const [internalQuery, setInternalQuery] = useState("");
  const query = props.value !== undefined ? props.value : internalQuery;
  const updateQuery = (v: string) => {
    if (props.value === undefined) setInternalQuery(v);
    props.onChange?.(v);
  };
  const debounced = useDebouncedValue(query, 250);
  const lastFiredRef = useRef<string | null>(null);

  // Fire onSearch / onClear when the debounced query settles on a new value.
  useEffect(() => {
    if (lastFiredRef.current === debounced) return;
    lastFiredRef.current = debounced;
    if (debounced.trim()) {
      props.onSearch(debounced);
    } else {
      props.onClear();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debounced]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Explicit Enter: fire immediately, bypassing the remaining debounce. We
    // always call onSearch — even on an empty box — so a date-only (no text)
    // search can run. The consuming form decides whether there are enough
    // criteria (e.g. a date range) to actually search.
    lastFiredRef.current = query;
    props.onSearch(query);
  };

  return (
    <form
      onSubmit={handleSubmit}
      className={clsx("relative flex-1", props.className)}
    >
      <MagnifyingGlassIcon className="absolute left-2.5 top-1/2 -translate-y-1/2 size-3.5 text-gray-500 pointer-events-none" />
      <input
        type="text"
        className="w-full pl-8 pr-8 py-1.5 text-xs bg-gray-900 text-gray-200 border border-gray-700 rounded focus:outline-none focus:border-squawk-blue placeholder:text-gray-500"
        value={query}
        onChange={(e) => updateQuery(e.target.value)}
        placeholder="Search..."
        autoFocus
      />
      {query && (
        <button
          type="button"
          onClick={() => updateQuery("")}
          className="absolute right-2 top-1/2 -translate-y-1/2 p-0.5 text-gray-500 hover:text-gray-300 transition-colors"
          aria-label="Clear search text"
        >
          <XMarkIcon className="size-3" />
        </button>
      )}
    </form>
  );
}

export default SearchBar;
